<template>
  <div class="info-item" :class="align">
    <!-- The icon is one line tall, so it sits on the first line when a long label wraps -->
    <View v-if="label" gap="sm" align="start" class="label">
      <Text v-if="icon" as="span" variant="body-medium" color="tertiary"><Icon :name="icon" size="small" /></Text>
      <Text variant="body-medium" color="secondary">{{ label }}</Text>
    </View>
    <!-- A value of its own, such as an avatar with a name, or the plain text -->
    <View v-if="$slots.default" gap="sm" align="center" class="value"><slot /></View>
    <Text v-else-if="value !== undefined && value !== null && value !== ''" variant="body-medium" class="value">
      {{ value }}
    </Text>
  </div>
</template>

<script setup lang="ts">
import { Icon, IconName, Text, View } from '@tallo/design-system';

/** One row of a details card: a label, with its icon, and the value beside it */
withDefaults(
  defineProps<{ label?: string; value?: string | number | null; icon?: IconName; align?: 'vertical' | 'horizontal' }>(),
  { label: undefined, value: undefined, icon: undefined, align: 'horizontal' },
);
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.info-item {
  width: 100%;
  display: grid;
  gap: var(--t-spacing-md);
  padding: var(--t-spacing-sm) 0;

  &.vertical {
    grid-template-columns: 1fr;
    gap: var(--t-spacing-xs);
  }

  &.horizontal {
    grid-template-columns: minmax(8rem, 11rem) 1fr;

    @include mobile {
      grid-template-columns: minmax(6rem, 9rem) 1fr;
    }
  }

  .label {
    min-width: 0;
  }

  .value {
    min-width: 0;
    white-space: break-spaces;
    overflow-wrap: anywhere;
  }
}
</style>
